import { Injectable, inject } from '@angular/core';
import {
  ApiDependencyProviderService,
  CollectionRequest,
  CollectionResponse,
  HttpParams,
} from '@web/common';
import { map, Observable } from 'rxjs';
import { blank, Guid, valueOrDefault } from '@web/utils';
import { WarehouseProductCollectionItemResponse } from '../responses/warehouse-product-collection-item.response';
import { WarehouseProductDetailsResponse } from '../responses/warehouse-product-details.response';
import { WarehouseProductUpdateRequest } from '../requests/warehouse-product-update.request';
import { WarehouseSkuUsageResponse } from '../responses/warehouse-sku-usage.response';
import { WarehouseProductQuantityHistoryCollectionItemResponse } from '../responses/warehouse-product-quantity-history-collection-item.response';

@Injectable({
  providedIn: 'root',
})
export class WarehouseProductApiService {
  private readonly provider = inject(ApiDependencyProviderService);

  /* prettier-ignore */
  public fetchCollection(
    data: {
      request: CollectionRequest,
      options?: {
        cacheable?: boolean;
        skipByLoading?: boolean;
      }
    }
  ): Observable<CollectionResponse<WarehouseProductCollectionItemResponse>> {
    const { request, options } = data;

    const path = `admin/good`;
    const url = this.provider.httpUrlFactory.withQueryParams(this.hostUrl(), path, this.provider.httpHelper.collectionRequestToQueryParams(request));
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .withOptionalSkipByLoadingInterceptor(() =>
        valueOrDefault(options?.skipByLoading, false),
      )
      .withOptionalCacheInterceptor(() =>
        valueOrDefault(options?.cacheable, false),
      )
      .build();

    return this.provider.http.get(url, { context }).pipe(
      map(
        this.provider.httpHelper.bodyToCollectionResponse(
          WarehouseProductCollectionItemResponse,
        ),
      ),
    );
  }

  public fetchQuantityHistoryCollection(params: {
    request: CollectionRequest;
    owner: string;
    id: Guid;
  }): Observable<
    CollectionResponse<WarehouseProductQuantityHistoryCollectionItemResponse>
  > {
    const path = `admin/good/${params.owner}/${HttpParams.Id}/quantity/history`;
    const url = this.provider.httpUrlFactory.complex((builder) =>
      builder
        .host(this.hostUrl())
        .path(path)
        .queryParams(
          this.provider.httpHelper.collectionRequestToQueryParams(
            params.request,
          ),
        )
        .params({
          [HttpParams.Id]: params.id,
        })
        .build(),
    );
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .skipByLoadingInterceptor()
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToCollectionResponse(
            WarehouseProductQuantityHistoryCollectionItemResponse,
          ),
        ),
      );
  }

  /* prettier-ignore */
  public fetchDetailsById(owner: string, id: Guid, cacheable = false): Observable<WarehouseProductDetailsResponse> {
    const path = `admin/good/${owner}/${HttpParams.Id}`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {[HttpParams.Id]: id});
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .skipByLoadingInterceptor()
      .withOptionalCacheInterceptor(() => cacheable)
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(map(this.provider.httpHelper.bodyToResponse(WarehouseProductDetailsResponse)));
  }

  /* prettier-ignore */
  public update(owner: string, id: Guid, request: WarehouseProductUpdateRequest): Observable<void> {
    const path = `admin/good/${owner}/${HttpParams.Id}`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, { [HttpParams.Id]: id });
    const body = this.provider.httpHelper.requestToBody(request, WarehouseProductUpdateRequest);
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .build();

    return this.provider.http.put(url, body, { context }).pipe(blank());
  }

  /* prettier-ignore */
  public fetchSkuUsage(sku: string): Observable<WarehouseSkuUsageResponse> {
    const path = `admin/good/sku/${HttpParams.SKU}`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, { [HttpParams.SKU]: sku });
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(map(this.provider.httpHelper.bodyToResponse(WarehouseSkuUsageResponse)));
  }

  /* prettier-ignore */
  public fetchProductLabel(owner: string, id: Guid): Observable<Blob> {
    const path = `admin/good/${owner}/${HttpParams.Id}/label/download`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, { [HttpParams.Id]: id });

    return this.provider.http.get(url, { responseType: 'blob' })
  }

  /* prettier-ignore */
  public fetchQuantityReport(owner: string): Observable<Blob> {
    const path = `admin/good/${owner}/report/quantity/download`;
    const url = this.provider.httpUrlFactory.simple(this.hostUrl(), path);

    return this.provider.http.get(url, { responseType: 'blob' });
  }

  private hostUrl(): string {
    return this.provider.environment.ApiHostUrl.Warehouse;
  }
}
